import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import {
  Search,
  Save,
  X,
  Plus,
  Edit3,
  RefreshCw,
  Users,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  Trash2,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import StudentVerificationService from '../../services/StudentVerificationService';

/**
 * CollegeRequirementsAdmin
 *
 * Comprehensive admin UI for managing college requirements across admissions cycles
 * - Edit deadlines, essays, requirements, and notes for global_colleges
 * - Push updates to all students with this college in their journey
 * - Manage application requirements, one-off tasks, and special notes
 */
const CollegeRequirementsAdmin = () => {
  const [colleges, setColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editing states
  const [editingDeadlines, setEditingDeadlines] = useState(false);
  const [editedDeadlines, setEditedDeadlines] = useState({});
  const [editingEssays, setEditingEssays] = useState(false);
  const [editedEssays, setEditedEssays] = useState([]);
  const [editingRequirements, setEditingRequirements] = useState(false);
  const [editedRequirements, setEditedRequirements] = useState([]);
  const [editingOneOffTasks, setEditingOneOffTasks] = useState(false);
  const [editedOneOffTasks, setEditedOneOffTasks] = useState([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [editingLinks, setEditingLinks] = useState(false);
  const [editedLinks, setEditedLinks] = useState({});
  const [editingDecisionDates, setEditingDecisionDates] = useState(false);
  const [editedDecisionDates, setEditedDecisionDates] = useState({});

  // Student impact tracking
  const [affectedStudents, setAffectedStudents] = useState([]);
  const [showPropagateModal, setShowPropagateModal] = useState(false);
  const [propagationType, setPropagationType] = useState(null);

  // Load all colleges
  useEffect(() => {
    loadColleges();
  }, []);

  // Filter colleges by search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredColleges(colleges);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredColleges(
        colleges.filter(c =>
          c.name?.toLowerCase().includes(query) ||
          c.location?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, colleges]);

  const loadColleges = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('global_colleges')
        .select('*')
        .order('name');

      if (error) throw error;
      setColleges(data || []);
    } catch (error) {
      console.error('Error loading colleges:', error);
      toast.error('Failed to load colleges');
    } finally {
      setLoading(false);
    }
  };

  const selectCollege = async (college) => {
    setSelectedCollege(college);

    // Load affected students count
    const { data: students, error } = await supabase
      .from('college_journey_colleges')
      .select('user_id')
      .eq('global_college_id', college.id);

    if (error) {
      console.error('Error loading affected students:', error);
    }
    setAffectedStudents(students || []);
  };

  // ============ DEADLINE HANDLERS ============
  const handleEditDeadlines = () => {
    setEditedDeadlines({
      ed_deadline: selectedCollege.ed_deadline || '',
      ed2_deadline: selectedCollege.ed2_deadline || '',
      ea_deadline: selectedCollege.ea_deadline || '',
      rd_deadline: selectedCollege.rd_deadline || '',
      priority_deadline: selectedCollege.priority_deadline || ''
    });
    setEditingDeadlines(true);
  };

  const handleSaveDeadlines = async () => {
    setPropagationType('deadlines');
    setShowPropagateModal(true);
  };

  const handlePropagateDeadlines = async () => {
    setSaving(true);
    try {
      // 1. Update global_colleges
      const { error: updateError } = await supabase
        .from('global_colleges')
        .update(editedDeadlines)
        .eq('id', selectedCollege.id);

      if (updateError) throw updateError;

      // 2. Propagate to all students with this college
      let successCount = 0;
      let errorCount = 0;

      for (const student of affectedStudents) {
        // Get the college_journey_college record
        const { data: cjcData } = await supabase
          .from('college_journey_colleges')
          .select('id')
          .eq('global_college_id', selectedCollege.id)
          .eq('user_id', student.user_id)
          .single();

        if (!cjcData) continue;

        // Update timeline items for each deadline that changed
        for (const [deadlineType, newDate] of Object.entries(editedDeadlines)) {
          if (newDate && newDate !== selectedCollege[deadlineType]) {
            const result = await StudentVerificationService.updateDeadline(
              cjcData.id,
              deadlineType,
              newDate,
              'admin'
            );
            if (result.success) {
              successCount++;
            } else {
              errorCount++;
            }
          }
        }
      }

      toast.success(`✓ Deadlines updated! Propagated to ${successCount} student timelines`, { duration: 5000 });

      if (errorCount > 0) {
        toast.error(`⚠️ ${errorCount} updates failed`, { duration: 5000 });
      }

      setEditingDeadlines(false);
      setShowPropagateModal(false);
      await loadColleges();
      await selectCollege({ ...selectedCollege, ...editedDeadlines });
    } catch (error) {
      console.error('Error saving deadlines:', error);
      toast.error('Failed to save deadlines');
    } finally {
      setSaving(false);
    }
  };

  // ============ ESSAY HANDLERS ============
  const handleEditEssays = () => {
    const essays = selectedCollege.essay_requirements?.essays || [];
    setEditedEssays(essays.map(essay => ({
      prompt: essay.prompt || '',
      wordLimit: essay.wordLimit || 650,
      title: essay.title || '',
      is_optional: essay.is_optional || false,
      // Preserve choice group fields
      choice_type: essay.choice_type || null,
      group_label: essay.group_label || '',
      options: essay.options || []
    })));
    setEditingEssays(true);
  };

  const handleAddEssay = () => {
    setEditedEssays(prev => [...prev, {
      prompt: '',
      wordLimit: 650,
      title: '',
      is_optional: false,
      choice_type: null, // null = independent, 'pick_one', 'optional_additional'
      group_label: '',
      options: [] // For choice groups
    }]);
  };

  const handleAddPromptOption = (essayIdx) => {
    setEditedEssays(prev => prev.map((essay, idx) => {
      if (idx === essayIdx) {
        return {
          ...essay,
          options: [...(essay.options || []), { option_id: String.fromCharCode(65 + (essay.options?.length || 0)), prompt: '', word_limit: essay.wordLimit }]
        };
      }
      return essay;
    }));
  };

  const handleUpdatePromptOption = (essayIdx, optionIdx, field, value) => {
    setEditedEssays(prev => prev.map((essay, idx) => {
      if (idx === essayIdx) {
        const updatedOptions = essay.options.map((opt, oIdx) =>
          oIdx === optionIdx ? { ...opt, [field]: value } : opt
        );
        return { ...essay, options: updatedOptions };
      }
      return essay;
    }));
  };

  const handleDeletePromptOption = (essayIdx, optionIdx) => {
    setEditedEssays(prev => prev.map((essay, idx) => {
      if (idx === essayIdx) {
        return { ...essay, options: essay.options.filter((_, oIdx) => oIdx !== optionIdx) };
      }
      return essay;
    }));
  };

  const handleUpdateEssay = (index, field, value) => {
    setEditedEssays(prev => prev.map((essay, idx) =>
      idx === index ? { ...essay, [field]: value } : essay
    ));
  };

  const handleDeleteEssay = (index) => {
    setEditedEssays(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveEssays = async () => {
    setPropagationType('essays');
    setShowPropagateModal(true);
  };

  const handlePropagateEssays = async () => {
    setSaving(true);
    try {
      // 1. Update global_colleges
      const updatedEssayReqs = {
        ...selectedCollege.essay_requirements,
        essays: editedEssays
      };

      const { error: updateError } = await supabase
        .from('global_colleges')
        .update({ essay_requirements: updatedEssayReqs })
        .eq('id', selectedCollege.id);

      if (updateError) throw updateError;

      // 2. Update college_journey_colleges.essay_requirements for all students with this college
      // This ensures EssaySlotService can see the choice group structure when regenerating slots
      const { error: cjcUpdateError } = await supabase
        .from('college_journey_colleges')
        .update({ essay_requirements: updatedEssayReqs })
        .eq('global_college_id', selectedCollege.id);

      if (cjcUpdateError) {
        console.error('Error updating college_journey_colleges:', cjcUpdateError);
        // Continue anyway - this is not critical
      }

      // 3. Regenerate essay slots for all students
      // Delete existing supplemental slots and regenerate from the new structure
      let successCount = 0;
      for (const student of affectedStudents) {
        try {
          const { data: cjcData } = await supabase
            .from('college_journey_colleges')
            .select('id')
            .eq('global_college_id', selectedCollege.id)
            .eq('user_id', student.user_id)
            .single();

          if (!cjcData) continue;

          // Delete existing supplemental essay slots for this college
          const { error: deleteError } = await supabase
            .from('essay_slots')
            .delete()
            .eq('college_id', cjcData.id)
            .eq('slot_type', 'supplemental');

          if (deleteError) {
            console.error(`Error deleting slots for student ${student.user_id}:`, deleteError);
            continue;
          }

          // Regenerate slots using EssaySlotService (which handles choice groups)
          const { default: EssaySlotService } = await import('../../services/EssaySlotService');
          await EssaySlotService.generateSlotsForCollege(student.user_id, cjcData.id);

          successCount++;
        } catch (studentError) {
          console.error(`Error processing student ${student.user_id}:`, studentError);
        }
      }

      toast.success(`✓ Essays updated! Regenerated slots for ${successCount} students`, { duration: 5000 });
      setEditingEssays(false);
      setShowPropagateModal(false);
      await loadColleges();
      await selectCollege({ ...selectedCollege, essay_requirements: updatedEssayReqs });
    } catch (error) {
      console.error('Error saving essays:', error);
      toast.error('Failed to save essays');
    } finally {
      setSaving(false);
    }
  };

  // ============ REQUIREMENTS HANDLERS ============
  const handleEditRequirements = () => {
    const reqs = selectedCollege.application_requirements || [];
    setEditedRequirements(reqs);
    setEditingRequirements(true);
  };

  const handleAddRequirement = () => {
    setEditedRequirements(prev => [...prev, { name: '', details: '', is_optional: false }]);
  };

  const handleUpdateRequirement = (index, field, value) => {
    setEditedRequirements(prev => prev.map((req, idx) =>
      idx === index ? { ...req, [field]: value } : req
    ));
  };

  const handleDeleteRequirement = (index) => {
    setEditedRequirements(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveRequirements = async () => {
    setSaving(true);
    try {
      const validReqs = editedRequirements.filter(req => req.name.trim());

      const { error } = await supabase
        .from('global_colleges')
        .update({ application_requirements: validReqs })
        .eq('id', selectedCollege.id);

      if (error) throw error;

      toast.success('✓ Requirements saved to global college data');
      setEditingRequirements(false);
      await loadColleges();
      await selectCollege({ ...selectedCollege, application_requirements: validReqs });
    } catch (error) {
      console.error('Error saving requirements:', error);
      toast.error('Failed to save requirements');
    } finally {
      setSaving(false);
    }
  };

  // ============ ONE-OFF TASKS HANDLERS ============
  const handleEditOneOffTasks = () => {
    setEditedOneOffTasks(selectedCollege.one_off_tasks || []);
    setEditingOneOffTasks(true);
  };

  const handleAddOneOffTask = () => {
    setEditedOneOffTasks(prev => [...prev, { title: '', description: '', category: 'application' }]);
  };

  const handleUpdateOneOffTask = (index, field, value) => {
    setEditedOneOffTasks(prev => prev.map((task, idx) =>
      idx === index ? { ...task, [field]: value } : task
    ));
  };

  const handleDeleteOneOffTask = (index) => {
    setEditedOneOffTasks(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveOneOffTasks = async () => {
    setSaving(true);
    try {
      const validTasks = editedOneOffTasks.filter(task => task.title.trim());

      const { error } = await supabase
        .from('global_colleges')
        .update({ one_off_tasks: validTasks })
        .eq('id', selectedCollege.id);

      if (error) throw error;

      toast.success('✓ Special tasks saved to global college data');
      setEditingOneOffTasks(false);
      await loadColleges();
      await selectCollege({ ...selectedCollege, one_off_tasks: validTasks });
    } catch (error) {
      console.error('Error saving one-off tasks:', error);
      toast.error('Failed to save special tasks');
    } finally {
      setSaving(false);
    }
  };

  // ============ NOTES HANDLERS ============
  const handleEditNotes = () => {
    setEditedNotes(selectedCollege.application_notes || '');
    setEditingNotes(true);
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('global_colleges')
        .update({ application_notes: editedNotes })
        .eq('id', selectedCollege.id);

      if (error) throw error;

      toast.success('✓ Application notes saved');
      setEditingNotes(false);
      await loadColleges();
      await selectCollege({ ...selectedCollege, application_notes: editedNotes });
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  // ============ LINKS HANDLERS ============
  const handleEditLinks = () => {
    setEditedLinks({
      college_website: selectedCollege.college_website || '',
      admissions_office_url: selectedCollege.admissions_office_url || '',
      financial_aid_url: selectedCollege.financial_aid_url || '',
      common_app_id: selectedCollege.common_app_id || ''
    });
    setEditingLinks(true);
  };

  const handleSaveLinks = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('global_colleges')
        .update(editedLinks)
        .eq('id', selectedCollege.id);

      if (error) throw error;

      toast.success('✓ Links saved successfully');
      setEditingLinks(false);
      await loadColleges();
      await selectCollege({ ...selectedCollege, ...editedLinks });
    } catch (error) {
      console.error('Error saving links:', error);
      toast.error('Failed to save links');
    } finally {
      setSaving(false);
    }
  };

  // ============ DECISION DATES HANDLERS ============
  const handleEditDecisionDates = () => {
    setEditedDecisionDates({
      ed_decision_date: selectedCollege.ed_decision_date || '',
      ea_decision_date: selectedCollege.ea_decision_date || '',
      rd_decision_date: selectedCollege.rd_decision_date || ''
    });
    setEditingDecisionDates(true);
  };

  const handleSaveDecisionDates = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('global_colleges')
        .update(editedDecisionDates)
        .eq('id', selectedCollege.id);

      if (error) throw error;

      toast.success('✓ Decision dates saved successfully');
      setEditingDecisionDates(false);
      await loadColleges();
      await selectCollege({ ...selectedCollege, ...editedDecisionDates });
    } catch (error) {
      console.error('Error saving decision dates:', error);
      toast.error('Failed to save decision dates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">College Requirements Admin</h1>
          <p className="text-sm text-stone-600">
            Manage deadlines, essays, requirements, and notes for any admissions cycle. Changes can be pushed to all students.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* College Selector - Left Column */}
          <div className="col-span-1 space-y-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-4">
                <Search className="w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search colleges..."
                  className="flex-1 text-sm border-none focus:outline-none"
                />
              </div>

              <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                {loading ? (
                  <div className="text-center py-8 text-stone-500">Loading...</div>
                ) : filteredColleges.length === 0 ? (
                  <div className="text-center py-8 text-stone-500 italic">No colleges found</div>
                ) : (
                  filteredColleges.map(college => (
                    <button
                      key={college.id}
                      onClick={() => selectCollege(college)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        selectedCollege?.id === college.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-stone-200 hover:border-indigo-300 hover:bg-indigo-50/50'
                      }`}
                    >
                      <div className="font-semibold text-sm text-slate-900">{college.name}</div>
                      <div className="text-xs text-stone-600 mt-1">{college.location}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Editor - Right 2 Columns */}
          <div className="col-span-2 space-y-6">
            {!selectedCollege ? (
              <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                <AlertCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Select a college to manage its requirements</p>
              </div>
            ) : (
              <>
                {/* College Header */}
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <h2 className="text-xl font-bold text-slate-900">{selectedCollege.name}</h2>
                  <p className="text-sm text-stone-600 mt-1">{selectedCollege.location}</p>
                  {affectedStudents.length > 0 && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-indigo-700">
                      <Users className="w-4 h-4" />
                      <span>{affectedStudents.length} student(s) have this college in their journey</span>
                    </div>
                  )}
                </div>

                {/* Deadlines Section */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-emerald-50/50 border-b-2 border-emerald-500 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-slate-900" />
                      <h3 className="text-md font-bold text-slate-900">Application Deadlines</h3>
                    </div>
                    {!editingDeadlines && (
                      <button
                        onClick={handleEditDeadlines}
                        className="px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="p-6">
                    {editingDeadlines ? (
                      <div className="space-y-4">
                        {[
                          { key: 'ed_deadline', label: 'Early Decision' },
                          { key: 'ed2_deadline', label: 'Early Decision II' },
                          { key: 'ea_deadline', label: 'Early Action' },
                          { key: 'rd_deadline', label: 'Regular Decision' },
                          { key: 'priority_deadline', label: 'Priority Deadline' }
                        ].map(({ key, label }) => (
                          <div key={key}>
                            <label className="text-xs text-stone-600 font-medium">{label}</label>
                            <input
                              type="date"
                              value={editedDeadlines[key] || ''}
                              onChange={(e) => setEditedDeadlines(prev => ({ ...prev, [key]: e.target.value }))}
                              className="w-full mt-1 p-2 text-sm border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        ))}

                        <div className="flex gap-3 pt-4 border-t">
                          <button
                            onClick={handleSaveDeadlines}
                            className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            Save & Push to Students
                          </button>
                          <button
                            onClick={() => setEditingDeadlines(false)}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { key: 'ed_deadline', label: 'Early Decision' },
                          { key: 'ed2_deadline', label: 'Early Decision II' },
                          { key: 'ea_deadline', label: 'Early Action' },
                          { key: 'rd_deadline', label: 'Regular Decision' },
                          { key: 'priority_deadline', label: 'Priority' }
                        ].map(({ key, label }) => (
                          <div key={key} className="bg-emerald-50/25 p-3 rounded border border-emerald-200">
                            <div className="text-xs text-stone-600 font-medium">{label}</div>
                            <div className="text-sm text-slate-900 mt-1">
                              {selectedCollege[key] || '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Essays Section */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-purple-50/50 border-b-2 border-purple-500 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-900" />
                      <h3 className="text-md font-bold text-slate-900">Supplemental Essays</h3>
                    </div>
                    {!editingEssays && (
                      <button
                        onClick={handleEditEssays}
                        className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="p-6">
                    {editingEssays ? (
                      <div className="space-y-4">
                        {editedEssays.map((essay, idx) => (
                          <div key={idx} className="bg-purple-50/25 rounded-lg p-4 border-2 border-purple-200">
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-stone-600 font-medium">Essay Title (Optional)</label>
                                <input
                                  type="text"
                                  value={essay.title}
                                  onChange={(e) => handleUpdateEssay(idx, 'title', e.target.value)}
                                  className="w-full mt-1 p-2 text-sm border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder='e.g., "Why Yale?"'
                                />
                              </div>

                              {/* Choice Type Selector */}
                              <div>
                                <label className="text-xs text-stone-600 font-medium">Prompt Type</label>
                                <select
                                  value={essay.choice_type || 'independent'}
                                  onChange={(e) => {
                                    const newType = e.target.value === 'independent' ? null : e.target.value;
                                    handleUpdateEssay(idx, 'choice_type', newType);
                                    if (newType && (!essay.options || essay.options.length === 0)) {
                                      // Auto-add 2 options when switching to choice group
                                      handleAddPromptOption(idx);
                                      handleAddPromptOption(idx);
                                    }
                                  }}
                                  className="w-full mt-1 p-2 text-sm border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                  <option value="independent">Single Prompt (Standard)</option>
                                  <option value="pick_one">Choice Group - Pick One (e.g., "Choose 1 of 3")</option>
                                  <option value="optional_additional">Choice Group - Optional Additional (e.g., "Optional essay on any topic")</option>
                                </select>
                              </div>

                              {/* Choice Group Fields */}
                              {essay.choice_type && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
                                  <div>
                                    <label className="text-xs text-blue-800 font-medium">Group Label</label>
                                    <input
                                      type="text"
                                      value={essay.group_label || ''}
                                      onChange={(e) => handleUpdateEssay(idx, 'group_label', e.target.value)}
                                      className="w-full mt-1 p-2 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder='e.g., "Community Essay (Choose One)"'
                                    />
                                    <p className="text-xs text-blue-600 mt-1">This label will be shown to students</p>
                                  </div>

                                  <div>
                                    <label className="text-xs text-blue-800 font-medium">Prompt Options</label>
                                    <div className="space-y-2 mt-2">
                                      {(essay.options || []).map((option, optIdx) => (
                                        <div key={optIdx} className="bg-white border border-blue-200 rounded p-3 space-y-2">
                                          <div className="flex justify-between items-center">
                                            <span className="text-xs font-mono text-blue-700">Option {option.option_id}</span>
                                            <button
                                              onClick={() => handleDeletePromptOption(idx, optIdx)}
                                              className="text-xs text-red-600 hover:text-red-700"
                                            >
                                              <X className="w-3 h-3" />
                                            </button>
                                          </div>
                                          <textarea
                                            value={option.prompt}
                                            onChange={(e) => handleUpdatePromptOption(idx, optIdx, 'prompt', e.target.value)}
                                            className="w-full p-2 text-xs border border-blue-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            rows="2"
                                            placeholder="Enter prompt text..."
                                          />
                                          <input
                                            type="number"
                                            value={option.word_limit || essay.wordLimit}
                                            onChange={(e) => handleUpdatePromptOption(idx, optIdx, 'word_limit', parseInt(e.target.value))}
                                            className="w-32 p-2 text-xs border border-blue-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Word limit"
                                          />
                                        </div>
                                      ))}
                                      <button
                                        onClick={() => handleAddPromptOption(idx)}
                                        className="w-full py-2 border border-dashed border-blue-300 rounded text-blue-600 hover:bg-blue-50 text-xs flex items-center justify-center gap-1"
                                      >
                                        <Plus className="w-3 h-3" />
                                        Add Option
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Standard Prompt (only show if not a choice group) */}
                              {!essay.choice_type && (
                                <div>
                                  <label className="text-xs text-stone-600 font-medium">Prompt</label>
                                  <textarea
                                    value={essay.prompt}
                                    onChange={(e) => handleUpdateEssay(idx, 'prompt', e.target.value)}
                                    className="w-full mt-1 p-2 text-sm border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    rows="3"
                                  />
                                </div>
                              )}

                              <div className="flex items-center gap-4">
                                <div className="flex-1">
                                  <label className="text-xs text-stone-600 font-medium">Word Limit {essay.choice_type && <span className="text-blue-600">(Default for all options)</span>}</label>
                                  <input
                                    type="number"
                                    value={essay.wordLimit}
                                    onChange={(e) => handleUpdateEssay(idx, 'wordLimit', e.target.value)}
                                    className="w-full mt-1 p-2 text-sm border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  />
                                </div>
                                <div className="flex items-center gap-2 pt-5">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={essay.is_optional}
                                      onChange={(e) => handleUpdateEssay(idx, 'is_optional', e.target.checked)}
                                      className="w-4 h-4 text-purple-600 rounded"
                                    />
                                    <span className="text-xs text-stone-700 font-medium">Optional</span>
                                  </label>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeleteEssay(idx)}
                                className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" />
                                Remove Essay
                              </button>
                            </div>
                          </div>
                        ))}

                        <button
                          onClick={handleAddEssay}
                          className="w-full py-2 border-2 border-dashed border-purple-300 rounded text-purple-700 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Essay
                        </button>

                        <div className="flex gap-3 pt-4 border-t">
                          <button
                            onClick={handleSaveEssays}
                            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            Save & Push to Students
                          </button>
                          <button
                            onClick={() => setEditingEssays(false)}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {(selectedCollege.essay_requirements?.essays || []).length > 0 ? (
                          <div className="space-y-3">
                            {selectedCollege.essay_requirements.essays.map((essay, idx) => (
                              <div key={idx} className="bg-purple-50/10 rounded-lg p-4 border border-purple-200">
                                <div className="font-semibold text-sm text-slate-900 mb-2">
                                  {essay.title || `Essay ${idx + 1}`}
                                  {essay.is_optional && <span className="ml-2 text-xs text-stone-600">(Optional)</span>}
                                  {essay.choice_type && (
                                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                      {essay.choice_type === 'pick_one' ? 'Pick One' : 'Optional Additional'}
                                    </span>
                                  )}
                                </div>

                                {/* Show choice group label and options */}
                                {essay.choice_type && essay.options?.length > 0 ? (
                                  <div className="space-y-2">
                                    {essay.group_label && (
                                      <p className="text-xs text-blue-800 font-medium bg-blue-50 p-2 rounded">{essay.group_label}</p>
                                    )}
                                    <div className="text-xs text-stone-600 mb-2">{essay.options.length} options available:</div>
                                    {essay.options.map((option, optIdx) => (
                                      <div key={optIdx} className="bg-white border border-blue-200 rounded p-2 text-xs">
                                        <span className="font-mono text-blue-700">Option {option.option_id}:</span> {option.prompt}
                                      </div>
                                    ))}
                                    <div className="text-xs text-stone-600">Word limit: {essay.wordLimit}</div>
                                  </div>
                                ) : (
                                  <>
                                    <p className="text-sm text-stone-700 mb-2">{essay.prompt}</p>
                                    <div className="text-xs text-stone-600">Word limit: {essay.wordLimit}</div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-stone-500 italic">No essays configured</div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Application Requirements Section */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-indigo-50/50 border-b-2 border-indigo-500 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-slate-900" />
                      <h3 className="text-md font-bold text-slate-900">Application Requirements</h3>
                    </div>
                    {!editingRequirements && (
                      <button
                        onClick={handleEditRequirements}
                        className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="p-6">
                    {editingRequirements ? (
                      <div className="space-y-4">
                        {editedRequirements.map((req, idx) => (
                          <div key={idx} className={`rounded-lg p-4 border-2 ${
                            req.is_optional ? 'bg-stone-50 border-stone-300' : 'bg-indigo-50/25 border-indigo-200'
                          }`}>
                            <div className="space-y-3">
                              <div className="flex items-start gap-2">
                                <div className="flex-1">
                                  <label className="text-xs text-stone-600 font-medium">Requirement Name</label>
                                  <input
                                    type="text"
                                    value={req.name}
                                    onChange={(e) => handleUpdateRequirement(idx, 'name', e.target.value)}
                                    className="w-full mt-1 p-2 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder='e.g., "Teacher Recommendations (2)"'
                                  />
                                </div>
                                <div className="flex items-center gap-2 pt-5">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={req.is_optional}
                                      onChange={(e) => handleUpdateRequirement(idx, 'is_optional', e.target.checked)}
                                      className="w-4 h-4 text-indigo-600 rounded"
                                    />
                                    <span className="text-xs text-stone-700 font-medium whitespace-nowrap">Optional</span>
                                  </label>
                                </div>
                              </div>
                              <div>
                                <label className="text-xs text-stone-600 font-medium">Details</label>
                                <textarea
                                  value={req.details}
                                  onChange={(e) => handleUpdateRequirement(idx, 'details', e.target.value)}
                                  className="w-full mt-1 p-2 text-sm border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  rows="2"
                                  placeholder="Additional details..."
                                />
                              </div>
                              <button
                                onClick={() => handleDeleteRequirement(idx)}
                                className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                              >
                                <X className="w-3 h-3" />
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}

                        <button
                          onClick={handleAddRequirement}
                          className="w-full py-2 border-2 border-dashed border-indigo-300 rounded text-indigo-700 hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Requirement
                        </button>

                        <div className="flex gap-3 pt-4 border-t">
                          <button
                            onClick={handleSaveRequirements}
                            disabled={saving}
                            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingRequirements(false)}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {(selectedCollege.application_requirements || []).length > 0 ? (
                          <div className="space-y-2">
                            {selectedCollege.application_requirements.map((req, idx) => (
                              <div key={idx} className={`rounded-lg p-3 border ${
                                req.is_optional ? 'bg-stone-50 border-stone-200' : 'bg-indigo-50/10 border-indigo-200'
                              }`}>
                                <div className="font-semibold text-sm text-slate-900">
                                  {req.name}
                                  {req.is_optional && <span className="ml-2 text-xs text-stone-600">(Optional)</span>}
                                </div>
                                {req.details && <div className="text-xs text-stone-600 mt-1">{req.details}</div>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-stone-500 italic">No requirements configured</div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Special/One-Off Tasks Section */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-amber-50/50 border-b-2 border-amber-500 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-slate-900" />
                      <h3 className="text-md font-bold text-slate-900">Special Requirements</h3>
                    </div>
                    {!editingOneOffTasks && (
                      <button
                        onClick={handleEditOneOffTasks}
                        className="px-3 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="p-6">
                    {editingOneOffTasks ? (
                      <div className="space-y-4">
                        {editedOneOffTasks.map((task, idx) => (
                          <div key={idx} className="bg-amber-50/25 rounded-lg p-4 border border-amber-200">
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-stone-600 font-medium">Task Title</label>
                                <input
                                  type="text"
                                  value={task.title}
                                  onChange={(e) => handleUpdateOneOffTask(idx, 'title', e.target.value)}
                                  className="w-full mt-1 p-2 text-sm border border-amber-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder='e.g., "The Rice Box"'
                                />
                              </div>
                              <div>
                                <label className="text-xs text-stone-600 font-medium">Description</label>
                                <textarea
                                  value={task.description}
                                  onChange={(e) => handleUpdateOneOffTask(idx, 'description', e.target.value)}
                                  className="w-full mt-1 p-2 text-sm border border-amber-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  rows="2"
                                />
                              </div>
                              <button
                                onClick={() => handleDeleteOneOffTask(idx)}
                                className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                              >
                                <X className="w-3 h-3" />
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}

                        <button
                          onClick={handleAddOneOffTask}
                          className="w-full py-2 border-2 border-dashed border-amber-300 rounded text-amber-700 hover:bg-amber-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Special Task
                        </button>

                        <div className="flex gap-3 pt-4 border-t">
                          <button
                            onClick={handleSaveOneOffTasks}
                            disabled={saving}
                            className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingOneOffTasks(false)}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {(selectedCollege.one_off_tasks || []).length > 0 ? (
                          <div className="space-y-2">
                            {selectedCollege.one_off_tasks.map((task, idx) => (
                              <div key={idx} className="bg-amber-50/10 rounded-lg p-3 border border-amber-200">
                                <div className="font-semibold text-sm text-slate-900">{task.title}</div>
                                <div className="text-xs text-stone-600 mt-1">{task.description}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-stone-500 italic">No special tasks configured</div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Application Notes Section */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-blue-50/50 border-b-2 border-blue-500 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-900" />
                      <h3 className="text-md font-bold text-slate-900">Application Notes</h3>
                    </div>
                    {!editingNotes && (
                      <button
                        onClick={handleEditNotes}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="p-6">
                    {editingNotes ? (
                      <div className="space-y-4">
                        <textarea
                          value={editedNotes}
                          onChange={(e) => setEditedNotes(e.target.value)}
                          className="w-full p-3 text-sm border-2 border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows="6"
                          placeholder="Important notes about this college's application process..."
                        />

                        <div className="flex gap-3">
                          <button
                            onClick={handleSaveNotes}
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingNotes(false)}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {selectedCollege.application_notes ? (
                          <div className="bg-blue-50/10 rounded-lg p-4 border border-blue-200">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">
                              {selectedCollege.application_notes}
                            </p>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-stone-500 italic">No notes configured</div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Important Links Section */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-cyan-50/50 border-b-2 border-cyan-500 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <ExternalLink className="w-5 h-5 text-slate-900" />
                      <h3 className="text-md font-bold text-slate-900">Important Links</h3>
                    </div>
                    {!editingLinks && (
                      <button
                        onClick={handleEditLinks}
                        className="px-3 py-1 text-xs bg-cyan-600 text-white rounded hover:bg-cyan-700 flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="p-6">
                    {editingLinks ? (
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs text-stone-600 font-medium">College Website</label>
                          <input
                            type="url"
                            value={editedLinks.college_website}
                            onChange={(e) => setEditedLinks(prev => ({ ...prev, college_website: e.target.value }))}
                            className="w-full mt-1 p-2 text-sm border border-cyan-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            placeholder="https://www.college.edu"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-stone-600 font-medium">Admissions Office URL</label>
                          <input
                            type="url"
                            value={editedLinks.admissions_office_url}
                            onChange={(e) => setEditedLinks(prev => ({ ...prev, admissions_office_url: e.target.value }))}
                            className="w-full mt-1 p-2 text-sm border border-cyan-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            placeholder="https://admissions.college.edu"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-stone-600 font-medium">Financial Aid URL</label>
                          <input
                            type="url"
                            value={editedLinks.financial_aid_url}
                            onChange={(e) => setEditedLinks(prev => ({ ...prev, financial_aid_url: e.target.value }))}
                            className="w-full mt-1 p-2 text-sm border border-cyan-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            placeholder="https://financialaid.college.edu"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-stone-600 font-medium">Common App ID</label>
                          <input
                            type="text"
                            value={editedLinks.common_app_id}
                            onChange={(e) => setEditedLinks(prev => ({ ...prev, common_app_id: e.target.value }))}
                            className="w-full mt-1 p-2 text-sm border border-cyan-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            placeholder="12345"
                          />
                          <p className="text-xs text-stone-500 mt-1">
                            Appended to: https://apply.commonapp.org/search/
                          </p>
                        </div>

                        <div className="flex gap-3 pt-4 border-t">
                          <button
                            onClick={handleSaveLinks}
                            disabled={saving}
                            className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingLinks(false)}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {(selectedCollege.college_website || selectedCollege.admissions_office_url || selectedCollege.financial_aid_url || selectedCollege.common_app_id) ? (
                          <div className="space-y-2">
                            {selectedCollege.college_website && (
                              <div className="bg-cyan-50/10 rounded-lg p-3 border border-cyan-200">
                                <div className="text-xs text-stone-600 font-medium">College Website</div>
                                <a href={selectedCollege.college_website} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-700 hover:text-cyan-800 hover:underline flex items-center gap-1">
                                  {selectedCollege.college_website}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            )}
                            {selectedCollege.admissions_office_url && (
                              <div className="bg-cyan-50/10 rounded-lg p-3 border border-cyan-200">
                                <div className="text-xs text-stone-600 font-medium">Admissions Office</div>
                                <a href={selectedCollege.admissions_office_url} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-700 hover:text-cyan-800 hover:underline flex items-center gap-1">
                                  {selectedCollege.admissions_office_url}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            )}
                            {selectedCollege.financial_aid_url && (
                              <div className="bg-cyan-50/10 rounded-lg p-3 border border-cyan-200">
                                <div className="text-xs text-stone-600 font-medium">Financial Aid</div>
                                <a href={selectedCollege.financial_aid_url} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-700 hover:text-cyan-800 hover:underline flex items-center gap-1">
                                  {selectedCollege.financial_aid_url}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            )}
                            {selectedCollege.common_app_id && (
                              <div className="bg-cyan-50/10 rounded-lg p-3 border border-cyan-200">
                                <div className="text-xs text-stone-600 font-medium">Common App</div>
                                <a href={`https://apply.commonapp.org/search/${selectedCollege.common_app_id}`} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-700 hover:text-cyan-800 hover:underline flex items-center gap-1">
                                  https://apply.commonapp.org/search/{selectedCollege.common_app_id}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-stone-500 italic">No links configured</div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Decision Dates Section */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-teal-50/50 border-b-2 border-teal-500 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-slate-900" />
                      <h3 className="text-md font-bold text-slate-900">Decision Notification Dates</h3>
                    </div>
                    {!editingDecisionDates && (
                      <button
                        onClick={handleEditDecisionDates}
                        className="px-3 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="p-6">
                    {editingDecisionDates ? (
                      <div className="space-y-4">
                        {[
                          { key: 'ed_decision_date', label: 'Early Decision' },
                          { key: 'ea_decision_date', label: 'Early Action' },
                          { key: 'rd_decision_date', label: 'Regular Decision' }
                        ].map(({ key, label }) => (
                          <div key={key}>
                            <label className="text-xs text-stone-600 font-medium">{label} Decision Date</label>
                            <input
                              type="date"
                              value={editedDecisionDates[key] || ''}
                              onChange={(e) => setEditedDecisionDates(prev => ({ ...prev, [key]: e.target.value }))}
                              className="w-full mt-1 p-2 text-sm border border-teal-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                          </div>
                        ))}

                        <div className="flex gap-3 pt-4 border-t">
                          <button
                            onClick={handleSaveDecisionDates}
                            disabled={saving}
                            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingDecisionDates(false)}
                            className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-4">
                        {[
                          { key: 'ed_decision_date', label: 'ED' },
                          { key: 'ea_decision_date', label: 'EA' },
                          { key: 'rd_decision_date', label: 'RD' }
                        ].map(({ key, label }) => (
                          <div key={key} className="bg-teal-50/25 p-3 rounded border border-teal-200">
                            <div className="text-xs text-stone-600 font-medium">{label} Decision</div>
                            <div className="text-sm text-slate-900 mt-1">
                              {selectedCollege[key] || '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Propagate Confirmation Modal */}
      {showPropagateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Confirm Propagation</h3>
            <p className="text-sm text-stone-700 mb-4">
              This will update <strong>{affectedStudents.length} student(s)</strong> who have this college in their journey.
            </p>
            <p className="text-sm text-stone-700 mb-6">
              {propagationType === 'deadlines' && 'All deadline-related timeline items will be updated with new dates.'}
              {propagationType === 'essays' && 'All essay slots and their 6 workflow tasks will be synchronized.'}
            </p>

            <div className="flex gap-3">
              <button
                onClick={propagationType === 'deadlines' ? handlePropagateDeadlines : handlePropagateEssays}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Propagating...</span>
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-4 h-4" />
                    <span>Push to Students</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setShowPropagateModal(false)}
                disabled={saving}
                className="px-4 py-2 bg-stone-200 text-stone-700 rounded hover:bg-stone-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeRequirementsAdmin;
